import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import {
  PERMISSIONS_AUCUNE,
  typeContextCourantToContextAppType,
  TypesContextesAutorisesEnum,
  type PermissionsApp,
  type TypeContextCourant,
} from './types'
import { useAuthentificationStore } from '@/features/auth'
import { useRouter, type RouteLocationRaw } from 'vue-router'


const NAVIGATION_ABORTED = 4 as const
export const useAppContextStore = defineStore('app-context', () => {
  const router = useRouter()
  const storeAuth = useAuthentificationStore()
  const { utilisateur } = storeToRefs(storeAuth)

  const typeContextSelectionne = ref<TypeContextCourant>(TypesContextesAutorisesEnum.Aucun)

  const contextesAutorises = computed<TypesContextesAutorisesEnum>(() => {
    let result = TypesContextesAutorisesEnum.Aucun
    result |= utilisateur.value?.contextsApp?.adherent
      ? TypesContextesAutorisesEnum.Adherent
      : TypesContextesAutorisesEnum.Aucun
    result |= utilisateur.value?.contextsApp?.prestataire
      ? TypesContextesAutorisesEnum.Prestataire
      : TypesContextesAutorisesEnum.Aucun
    return result
  })

  const contextCourant = computed<TypeContextCourant>(() => {
    let selected = contextesAutorises.value & typeContextSelectionne.value
    // Définie une valeur par défaut
    if (selected === TypesContextesAutorisesEnum.Aucun) {
      if (contextesAutorises.value & TypesContextesAutorisesEnum.Adherent) {
        selected = TypesContextesAutorisesEnum.Adherent
      } else if (contextesAutorises.value & TypesContextesAutorisesEnum.Prestataire) {
        selected = TypesContextesAutorisesEnum.Prestataire
      }
    }
    return selected
  })

  function setContextCourant(value: TypeContextCourant) {
    if (autoriseContext(value)) {
      typeContextSelectionne.value = contextesAutorises.value & value
    }
  }

  const permissions = computed<PermissionsApp>(() => {
    if (contextCourant.value === TypesContextesAutorisesEnum.Adherent) {
      return utilisateur.value!.contextsApp!.adherent!
    } else if (contextCourant.value === TypesContextesAutorisesEnum.Prestataire) {
      return utilisateur.value!.contextsApp!.prestataire!
    } else {
      return PERMISSIONS_AUCUNE
    }
  })

  function autoriseContext(context: TypeContextCourant) {
    return (contextesAutorises.value & context) === context
  }

  function toggleContext() {
    const nouveauContext =
      contextCourant.value === TypesContextesAutorisesEnum.Prestataire
        ? TypesContextesAutorisesEnum.Adherent
        : TypesContextesAutorisesEnum.Prestataire

    if (autoriseContext(nouveauContext) === false) return

    const currentRoute = router.currentRoute.value
    if (currentRoute.meta.requiresContext !== true) {
      setContextCourant(nouveauContext)
      return
    }

    // Calcule nouvelle route
    const toggleContextResolver = currentRoute.meta.toggleContextResolver
    let newRoute: RouteLocationRaw = { name: 'home' }

    if (toggleContextResolver) {
      if (typeof toggleContextResolver === 'string') {
        if (toggleContextResolver[0] === '/') {
          newRoute = toggleContextResolver
        } else {
          const params = {
            context: typeContextCourantToContextAppType(nouveauContext),
          }
          newRoute = { name: toggleContextResolver, params }
          router.push(newRoute)
          return
        }
      } else {
        newRoute = toggleContextResolver(currentRoute, nouveauContext, typeContextCourantToContextAppType(nouveauContext))
        router.push(newRoute)
        return
      }
    }
    router.push(newRoute).then((value) => {
      if (value?.type !== NAVIGATION_ABORTED) {
        setContextCourant(nouveauContext)
      }
    })
  }

  return {
    contextCourant,
    contextesAutorises,
    permissions,
    autoriseContext,
    setContextCourant,
    toggleContext,
  }
})

